// SF3 — where the month stands, and what it is heading for.
//
// The forecast is a range, not a number, and the status only changes when the
// whole range falls outside the budget. A range that straddles the line is not
// news, and alerting on it would train the seller to ignore the alert that is.
import React from 'react';
import s from '../BudgetOptimiser.module.css';
import { ENV_CLASS, money } from '../format';

export default function Envelope({ plan, currency }) {
  const env = plan?.envelope || {};
  const forecast = plan?.forecast || {};
  if (env.state === 'no budget set') {
    return <p className={s.empty}>{env.why}</p>;
  }
  const budget = env.monthly_budget || 0;
  const spent = (env.spent || {}).total || 0;
  const reserve = env.event_reserve || 0;
  const rest = forecast.rest_of_month_low || 0;
  const scale = Math.max(budget, env.projected_high || 0) || 1;
  const width = (value) => `${Math.max(0, Math.min(100, (value / scale) * 100))}%`;

  return (
    <div>
      <div className={s.stats}>
        <div className={s.stat}>
          <span className={s.statValue}>{money(budget, currency)}</span>
          <span className={s.statLabel}>your monthly budget</span>
        </div>
        <div className={s.stat}>
          <span className={s.statValue}>{money(spent, currency)}</span>
          <span className={s.statLabel}>spent so far</span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statValue} ${s[ENV_CLASS[env.state] || '']}`}>
            {money(env.projected_low, currency)}–{money(env.projected_high, currency)}
          </span>
          <span className={s.statLabel}>heading for · {env.state}</span>
        </div>
        <div className={s.stat}>
          <span className={s.statValue}>{env.days_left}</span>
          <span className={s.statLabel}>days left</span>
        </div>
      </div>

      <div className={s.envelopeBar}>
        <div className={s.envSpent} style={{ width: width(spent) }} />
        <div className={s.envForecast} style={{ width: width(rest) }} />
        {reserve > 0 && (
          <div className={s.envReserve} style={{ width: width(reserve) }} />
        )}
      </div>
      <div className={s.envLegend}>
        <span><i className={s.envDot} style={{ background: '#2F6FED' }} />spent</span>
        <span><i className={s.envDot} style={{ background: '#9FB8EF' }} />
          rest of the month</span>
        {reserve > 0 && (
          <span><i className={s.envDot} style={{ background: '#E09A28' }} />
            held for events</span>
        )}
      </div>
      <p className={s.coverage}>{env.why}</p>
      {(env.spent || {}).note && <p className={s.coverage}>{env.spent.note}</p>}
      {(forecast.notes || []).map((note) => (
        <p key={note} className={s.coverage}>{note}</p>
      ))}
      {plan?.underspend?.why && <p className={s.coverage}>{plan.underspend.why}</p>}

      {!!(forecast.campaigns || []).length && (
        <>
          <h3 className={s.sectionTitle}>What each campaign will cost</h3>
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Campaign</th><th className={s.right}>Budget a day</th>
                  <th className={s.right}>Likely a day</th>
                  <th className={s.right}>Rest of month</th>
                  <th>How it was worked out</th>
                </tr>
              </thead>
              <tbody>
                {forecast.campaigns.map((row) => (
                  <tr key={row.campaign_id}>
                    <td>{row.name}</td>
                    <td className={s.right}>{money(row.budget, currency)}</td>
                    <td className={s.right}>{money(row.per_day, currency)}</td>
                    <td className={s.right}>
                      {money(row.rest_of_month, currency)}
                      <div className={s.thin}>
                        up to {money(row.with_rollover, currency)}
                      </div>
                    </td>
                    <td className={s.thin}>{row.basis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={s.coverage}>
            The higher figure allows for Amazon letting a busy day run over budget using
            earlier unspent days.
          </p>
        </>
      )}
    </div>
  );
}
